// web/src/db.js
// Dexie v4 — local IndexedDB schema for ServeIQ PWA
// KEY FIXES:
//   - db.venue_tables (NOT db.tables — conflicts with Dexie built-in)
//   - active filter uses .filter(i => i.active !== false) NOT .where('active').equals(1)

import Dexie from 'dexie';

export const db = new Dexie('serveiq');

db.version(1).stores({
  // NOTE: named venue_tables to avoid conflict with Dexie's built-in .tables property
  venue_tables: 'id, status, active',
  menu_categories: 'id, sort_order, active',
  menu_items: 'id, category_id, active, name',
  tickets: 'id, table_id, status, waiter_id, synced',
  ticket_lines: 'id, ticket_id, synced, voided',
  staff: 'id, phone, role, active',
  sync_queue: '++id, type, status, created_at',
  payments: 'id, ticket_id, method, status',
});

// ─── Typed accessors ─────────────────────────────────────────────────────────
// Use these helpers everywhere to avoid querying wrong table names

export const Tables = {
  getAll: () => db.venue_tables.filter(t => t.active !== false).toArray(),
  getById: (id) => db.venue_tables.get(id),
  getFree: () => db.venue_tables.filter(t => t.active !== false && t.status === 'free').toArray(),
  upsert: (table) => db.venue_tables.put(table),
  upsertMany: (tables) => db.venue_tables.bulkPut(tables),
  setStatus: (id, status) => db.venue_tables.update(id, { status }),
};

export const Menu = {
  getCategories: () =>
    db.menu_categories.filter(c => c.active !== false).sortBy('sort_order'),
  getItems: () =>
    db.menu_items.filter(i => i.active !== false).toArray(),
  getItemsByCategory: (categoryId) =>
    db.menu_items.filter(i => i.active !== false && i.category_id === categoryId).toArray(),
  upsertCategories: (cats) => db.menu_categories.bulkPut(cats),
  upsertItems: (items) => db.menu_items.bulkPut(items),
};

export const Tickets = {
  getActive: () =>
    db.tickets.filter(t => ['open','sent','ready'].includes(t.status)).toArray(),
  getById: (id) => db.tickets.get(id),
  getByTable: (tableId) =>
    db.tickets.filter(t => t.table_id === tableId && t.status !== 'closed').first(),
  upsert: (ticket) => db.tickets.put(ticket),
  setStatus: (id, status) => db.tickets.update(id, { status }),
};

export const Lines = {
  getByTicket: (ticketId) =>
    db.ticket_lines.filter(l => l.ticket_id === ticketId && !l.voided).toArray(),
  upsert: (line) => db.ticket_lines.put(line),
  upsertMany: (lines) => db.ticket_lines.bulkPut(lines),
  void: (id) => db.ticket_lines.update(id, { voided: true }),
};

// ─── Sync queue ───────────────────────────────────────────────────────────────

export const SyncQueue = {
  add: (type, payload) =>
    db.sync_queue.add({ type, payload, status: 'pending', created_at: Date.now() }),
  getPending: () =>
    db.sync_queue.filter(i => i.status === 'pending').toArray(),
  markDone: (id) =>
    db.sync_queue.update(id, { status: 'done', synced_at: Date.now() }),
  markFailed: (id, error) =>
    db.sync_queue.update(id, { status: 'failed', error }),
};

// ─── Seed / hydrate from API ─────────────────────────────────────────────────

export async function hydrateFromAPI(apiClient) {
  try {
    const [tablesRes, menuRes] = await Promise.all([
      apiClient.get('/tables'),
      apiClient.get('/menu'),
    ]);

    await db.transaction('rw', db.venue_tables, db.menu_categories, db.menu_items, async () => {
      await Tables.upsertMany(tablesRes.data);
      await Menu.upsertCategories(menuRes.data.categories);
      await Menu.upsertItems(menuRes.data.items);
    });

    console.log('[Dexie] Hydrated from API');
  } catch (err) {
    console.warn('[Dexie] Hydration failed (offline?):', err.message);
  }
}

export default db;
